// Confirm releasing the hardware from a screen.
//
// Confirmed rather than one-click because the screen goes dark immediately —
// but deliberately NOT type-to-confirm like deletion. Nothing is lost here:
// the screen, its content and its licence all stay, and re-attaching hardware
// is a normal pairing. Matching deletion's friction would teach operators that
// the two are equally severe, which is exactly the confusion this action
// exists to remove.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Unplug } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { detachScreen } from '@meno/ui/lib/api'

export default function DetachScreenDialog({
  open, onClose, publicId, screenName, onDetached,
}: {
  open: boolean
  onClose: () => void
  publicId: string
  screenName: string
  onDetached?: () => void
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)

  const confirm = async () => {
    setBusy(true)
    try {
      await detachScreen(publicId)
      toast.success(t('screens.detachDone', 'Device detached'))
      onClose()
      onDetached?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('screens.detachFailed', 'Could not detach the device.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o && !busy) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Unplug className="w-4 h-4" />
            {t('screens.detachTitle', 'Detach device')}
          </DialogTitle>
          <DialogDescription>
            {t('screens.detachConfirm',
              'The device attached to "{{name}}" will stop showing content and drop to a pairing code.',
              { name: screenName })}
          </DialogDescription>
        </DialogHeader>

        {/* Say what is kept, not just what happens. The reason operators reach
            for Delete when they only meant to swap hardware is that nothing
            ever told them a lighter option preserves their work. */}
        <ul className="text-sm text-muted-foreground space-y-1.5 list-disc ps-5">
          <li>{t('screens.detachKeep1', 'This screen, its name and its branch are kept.')}</li>
          <li>{t('screens.detachKeep2', 'Published content stays assigned to it.')}</li>
          <li>{t('screens.detachKeep3', 'The licence stays with the screen — no seat is used up.')}</li>
        </ul>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={confirm} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />}
            {t('screens.detach', 'Detach')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
